import { computed, reactive, ref } from "vue";
import type {
  DeliveryAvailabilityData,
  DeliveryArea,
  KanaStepA,
  Member,
  SlipPrintData,
  TagPrintData,
  ZipAddressName,
} from "../types";
import type {
  DeliveryRequestCreateInput,
  MemberCreateInput,
  MemberUpdateInput,
  OperationInput,
  TmpMemberUpdateInput,
} from "@/generated/graphql";
import deliveryRegisterInputStore from "@/store/deliveryRegisterInputStore";
import { localStorageGetUserId } from "@/common/localStorageUtil";

/**
 * ストアに保持した配達便JSONを画面用の配列へ変換する。
 */
const parseDeliveryAvailability = (
  json: string
): DeliveryAvailabilityData[][] => {
  if (!json) {
    return [];
  }
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * 配達依頼登録画面で利用する状態オブジェクトを初期化して返す composable。
 */
export const useDeliveryRequestRegisterState = () => {
  /**
   * 文言データ
   */
  const wordList = ref<Record<string, string>>({});
  const isWordListReady = computed(
    () => Object.keys(wordList.value).length > 0
  );
  const dummy_payment_amount = ref("");

  /**
   * 前画面からデータ受け渡し
   */
  const pref_name = ref(deliveryRegisterInputStore.pref_name);
  const zip_code = ref(deliveryRegisterInputStore.zip_code);
  const user_id = ref(deliveryRegisterInputStore.user_id);
  const deliveryAvailabilityStore = ref<DeliveryAvailabilityData[][]>(
    parseDeliveryAvailability(deliveryRegisterInputStore.deliveryJson)
  );
  const pref_name_befor = ref("");

  /**
   * 店舗情報
   */
  const shopData = ref<
    { name: string; shippingLabelPrintFlg: number; deliveryOrderPrintFlg: number } | null
  >(null);

  /**
   * 仮会員データ
   */
  const tmpMemberData = ref<
    | {
        __typename?: "TmpMember";
        address1: string;
        address2: string;
        address3?: string | null | undefined;
        name1: string;
        name2: string;
        name_kana1: string;
        name_kana2: string;
        zip_code: string;
        phone: string;
        shop_code?: string | null | undefined;
      }
    | null
    | undefined
  >(null);

  /**
   * 個口数リスト
   */
  const unitItems = ref<number[]>([]);

  /**
   * 決済方法
   */
  const paymentMethodItems = ref<Array<string | undefined>>([]);
  const paymentRequiredFlg = ref(false);

  const btnNonMemberToggle = ref(0);
  const userErrorMessage = ref("");
  const errorPrefSelect = ref("");

  /**
   * 住所リスト
   */
  const zipAddressList = ref<
    Array<ZipAddressName | null | undefined> | null | undefined
  >(null);
  const zipCodeFromPref = ref("");
  const cityBlockList = ref<KanaStepA | null | undefined>(null);
  const originalAddress = ref("");
  const originalSectionAddress = ref("");
  const originalCityAddress = ref("");
  const originalBlockName = ref("");
  const sectionBlockList = ref<KanaStepA | null | undefined>(null);
  const blockList = ref<
    Array<{ name: string | null | undefined; enabled: boolean } | null | undefined>
  >([]);
  const unassignedBlockList = ref<Array<string | null | undefined>>([]);
  const addressBlockList = ref<Array<string | null> | null | undefined>([]);
  const prefListItems = ref<Array<string | null> | null | undefined>(null);

  /**
   * 会員データ
   */
  const memberData = ref<Member | null | undefined>(null);

  // 配達エリア名
  const listDeliveryArea = ref<DeliveryArea[] | null>(null);
  const deliveryAreaName = ref("");
  const prefSelectValue = ref("");
  const acceptableEndTime = ref("");

  const tagPrintShippingData = ref<TagPrintData[]>([]);
  const slipPrintShippingData = ref<SlipPrintData[]>([]);

  /**
   * 配達開始時間の選択ボックス
   */
  const deliveryStartHourItem = ref<number[]>([]);
  const deliveryStartHourValues = ref<number | undefined>(undefined);
  const deliveryStartMinitItem = ref([
    { label: "00", deliveryStartMinit: 0 },
    { label: "30", deliveryStartMinit: 1 },
  ]);
  const deliveryStartMinitValues = ref<
    { label: string; deliveryStartMinit: number } | undefined
  >(undefined);

  // 配達希望時間
  const irregularDeliveryStartTime = ref("");
  const irregularDeliveryEndTime = ref("");
  const irregularDeliveryDisplayTime = ref("");
  const deliveryId_0_Flg = ref(false);
  const deliveryStartTime = ref("");
  const deliveryEndTime = ref("");
  const overDeliveryEndTime = ref(false);

  // ２件分配達
  const doubleDeliOriconCount = ref<number | null>(null);
  const isDoubleDeli = ref(false);
  // チャーター便
  const charterDeliOriconCount = ref<number | null>(null);
  const isCharterDeli = ref(false);

  // 配達便情報のヘッダー
  const deliveryAvailabilityHeader = ref<Map<string, string>>(new Map());
  const deliveryAvailabilityData = ref<DeliveryAvailabilityData[][]>([]);
  const disp_deliveryAvailabilityData = ref<DeliveryAvailabilityData[][]>([]);
  const displayDeliveryTime = ref("");
  const deliverySelectDayBox = ref("-999");
  const deliverySelectBox = ref("-999");

  // 都度便の場合に配達希望時間を非表示とするフラグ
  const is_hide_delivery_time_on_spot = ref(0);

  const overlay = ref(false);
  const errdialog = ref(false);
  const errMessage = ref("");
  const areaDialog = ref(deliveryRegisterInputStore.areaDialog);
  const getAvailableDate = ref(deliveryRegisterInputStore.getAvailableDate);
  const backToStart = ref(false);
  const addressFixed = ref(false);
  const addressDialog = ref(false);
  const commaPyamentAmount = ref("");
  const errorZipCode = ref("");

  // ハウスカード
  const house_card_display = ref(0);
  const house_card_validation = ref("");
  const house_card_required = ref(0);
  const house_card_change = ref(false);
  const house_card_before = ref("");

  // レジ・レシートNo
  const register_no_max_length = ref(0);
  const register_no_validation = ref("");
  const receipt_no_max_length = ref(0);
  const receipt_no_validation = ref("");

  // 置換前の備考蘭
  const memberNotesBefore = ref("");
  const deliveryNotesBefore = ref("");
  const messageMemberNotes = ref("");
  const messageDeliveryNotes = ref("");

  const clickMode = ref(false);

  // 配達依頼作成 input
  const deliveryRequestCreateInput = reactive<DeliveryRequestCreateInput>({
    area_code: "",
    chain_code: "",
    customer_code: "",
    delivery_address1: pref_name.value,
    delivery_address2: "",
    delivery_address3: "",
    delivery_id: 9999,
    delivery_name1: "",
    delivery_name2: "",
    delivery_name_kana1: "",
    delivery_name_kana2: "",
    delivery_notes: "",
    delivery_phone: "",
    delivery_zip_code: zip_code.value,
    irregular_delivery_start_time: "",
    irregular_delivery_end_time: "",
    member_notes: "",
    payment_amount: null,
    payment_method: "",
    required_delivery_date: "",
    shop_code: "",
    unit: 1,
    house_card: "",
    double_deli_oricon_flg: 0,
    charter_deli_oricon_flg: 0,
  });

  // 会員作成 input
  const memberCreateInput = reactive<MemberCreateInput>({
    address1: "",
    address2: "",
    address3: "",
    chain_code: "",
    customer_code: "",
    name1: "",
    name2: "",
    name_kana1: "",
    name_kana2: "",
    notes: "",
    phone: "",
    shop_code: "",
    zip_code: "",
    house_card: "",
  });

  // 会員更新 input
  const memberUpdateInput = reactive<MemberUpdateInput>({
    customer_code: "",
    chain_code: "",
    address1: "",
    address2: "",
    address3: "",
    member_id: "",
    name1: "",
    name2: "",
    name_kana1: "",
    name_kana2: "",
    search_kana: "",
    notes: "",
    phone: "",
    shop_code: "",
    zip_code: "",
    house_card: "",
  });

  // 仮会員更新 input
  const tmpMemberUpdateInput = reactive<TmpMemberUpdateInput>({
    tmp_member_id: "",
  });

  // 配達依頼作成 logInput
  const operationInput = reactive<OperationInput>({
    area_code: "10",
    detail: "操作詳細",
    display_category: "画面名",
    operation: "操作",
    user_id: localStorageGetUserId(),
  });

  return {
    wordList,
    isWordListReady,
    dummy_payment_amount,
    pref_name,
    zip_code,
    user_id,
    deliveryAvailabilityStore,
    pref_name_befor,
    shopData,
    tmpMemberData,
    unitItems,
    paymentMethodItems,
    paymentRequiredFlg,
    btnNonMemberToggle,
    userErrorMessage,
    errorPrefSelect,
    zipAddressList,
    zipCodeFromPref,
    cityBlockList,
    originalAddress,
    originalSectionAddress,
    originalCityAddress,
    originalBlockName,
    sectionBlockList,
    blockList,
    unassignedBlockList,
    addressBlockList,
    prefListItems,
    memberData,
    listDeliveryArea,
    deliveryAreaName,
    prefSelectValue,
    acceptableEndTime,
    tagPrintShippingData,
    slipPrintShippingData,
    deliveryStartHourItem,
    deliveryStartHourValues,
    deliveryStartMinitItem,
    deliveryStartMinitValues,
    irregularDeliveryStartTime,
    irregularDeliveryEndTime,
    irregularDeliveryDisplayTime,
    deliveryId_0_Flg,
    deliveryStartTime,
    deliveryEndTime,
    overDeliveryEndTime,
    doubleDeliOriconCount,
    isDoubleDeli,
    charterDeliOriconCount,
    isCharterDeli,
    deliveryAvailabilityHeader,
    deliveryAvailabilityData,
    disp_deliveryAvailabilityData,
    displayDeliveryTime,
    deliverySelectDayBox,
    deliverySelectBox,
    is_hide_delivery_time_on_spot,
    overlay,
    errdialog,
    errMessage,
    areaDialog,
    getAvailableDate,
    backToStart,
    addressFixed,
    addressDialog,
    commaPyamentAmount,
    errorZipCode,
    house_card_display,
    house_card_validation,
    house_card_required,
    house_card_change,
    house_card_before,
    register_no_max_length,
    register_no_validation,
    receipt_no_max_length,
    receipt_no_validation,
    memberNotesBefore,
    deliveryNotesBefore,
    messageMemberNotes,
    messageDeliveryNotes,
    clickMode,
    deliveryRequestCreateInput,
    memberCreateInput,
    memberUpdateInput,
    tmpMemberUpdateInput,
    operationInput,
  };
};
